/**
 * chatApi.js
 * Sends chat messages to the HireNeuron FastAPI backend.
 *
 * Supports multi-agent architecture:
 * - fetchAgents()         — Get available agents (default + custom)
 * - streamChatMessage()   — SSE streaming with agent selection
 * - sendChatMessage()     — Traditional JSON request/response (fallback)
 *
 * Reads auth tokens from localStorage keys 'Accesstoken' and 'Refreshtoken'
 * which is what app.hireneuron.com stores after login.
 */

//const API_BASE_URL = process.env.REACT_APP_AI_BACKEND_URL
//const API_BASE_URL = "http://127.0.0.1:8000";
//const API_BASE_URL = "https://chatapi-720029934636.asia-southeast1.run.app/";
//const API_BASE_URL = "chatapi.hireneuron.com";
const API_BASE_URL = (process.env.REACT_APP_AI_BACKEND_URL || "").replace(
  /\/+$/,
  "",
);

/**
 * Reads tokens and user info from localStorage.
 */
function getTokens() {
  let accessToken = "";
  let refreshToken = "";

  try {
    const authUserStr = localStorage.getItem("authUser");
    if (authUserStr) {
      const authUser = JSON.parse(authUserStr);
      accessToken =
        authUser.token ||
        authUser.accessToken ||
        authUser.jwtToken ||
        authUser.access_token ||
        "";
      refreshToken = authUser.refreshToken || authUser.refresh_token || "";
    }
  } catch (e) {
    console.warn("Error parsing authUser from localStorage", e);
  }

  // Fallback to direct keys just in case
  if (!accessToken) {
    accessToken =
      localStorage.getItem("Accesstoken") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      "";
  }

  if (!refreshToken) {
    refreshToken =
      localStorage.getItem("Refreshtoken") ||
      localStorage.getItem("refreshToken") ||
      localStorage.getItem("refresh_token") ||
      "";
  }

  return { accessToken, refreshToken };
}

/**
 * Build common headers for API calls.
 */
function buildHeaders(options = {}) {
  const { accessToken, refreshToken } = getTokens();

  let defaultUserId = "1";
  let defaultCompanyId = "1";
  try {
    const authUserStr = localStorage.getItem("authUser");
    if (authUserStr) {
      const authUser = JSON.parse(authUserStr);
      // Align with api_helper.js: userId is often stored in 'clientId'
      if (authUser.clientId) defaultUserId = authUser.clientId.toString();
      else if (authUser.userId) defaultUserId = authUser.userId.toString();
      else if (authUser.id) defaultUserId = authUser.id.toString();

      if (authUser.companyId) defaultCompanyId = authUser.companyId.toString();
      else if (authUser.clientId && !authUser.companyId)
        defaultCompanyId = authUser.clientId.toString();
    }
  } catch (e) {}

  // Extract userId and companyId explicitly from the Access Token's 'sub' field
  if (accessToken) {
    try {
      const payloadBase64 = accessToken.split(".")[1];
      const decodedJson = JSON.parse(atob(payloadBase64));

      if (decodedJson.sub && decodedJson.sub.includes("#")) {
        const parts = decodedJson.sub.split("#");
        if (parts[0]) defaultUserId = parts[0];
        if (parts[1]) defaultCompanyId = parts[1];
      }
    } catch (error) {
      console.warn("Failed to decode token for subject", error);
    }
  }

  const userId = options.userId || defaultUserId;
  const companyId = options.companyId || defaultCompanyId;

  return {
    "Content-Type": "application/json",
    Accesstoken: accessToken,
    Refreshtoken: refreshToken,
    "x-user-id": userId,
    "x-company-id": companyId,
  };
}

/**
 * Fetch available agents from the backend.
 *
 * @param {Object} options - Optional overrides: { companyId }
 * @returns {Promise<Array>} - List of agent objects: { id, agentName, description, agentType, starterQuestions }
 */
export async function fetchAgents(options = {}) {
  const headers = buildHeaders(options);
  const companyId = headers["x-company-id"] || "1";

  try {
    const response = await fetch(
      `${API_BASE_URL}/getAgents?companyId=${companyId}`,
      {
        headers,
      },
    );

    if (!response.ok) {
      console.warn(`[ChatAPI] fetchAgents failed: ${response.status}`);
      return [];
    }

    return await response.json();
  } catch (err) {
    console.warn("[ChatAPI] fetchAgents error:", err);
    return [];
  }
}

/**
 * Stream a chat message via SSE — receives step-by-step updates.
 *
 * @param {string}   message              - The user's message
 * @param {Array}    conversationHistory   - Previous messages [{role, content}]
 * @param {Function} onStep               - Callback fired for each SSE step event
 * @param {Object}   options              - Optional overrides: { userId, companyId, agentId, agentType }
 * @returns {Promise<Object>}             - Final "done" response: { reply, action, memory, data }
 */
export async function streamChatMessage(
  message,
  conversationHistory = [],
  onStep,
  options = {},
) {
  const headers = buildHeaders(options);
  console.log("[ChatAPI] Stream headers:", {
    "x-user-id": headers["x-user-id"],
    "x-company-id": headers["x-company-id"],
    Accesstoken: headers.Accesstoken
      ? "..." + headers.Accesstoken.slice(-10)
      : "NONE",
    Refreshtoken: headers.Refreshtoken
      ? "..." + headers.Refreshtoken.slice(-10)
      : "NONE",
    agentId: options.agentId || null,
    agentType: options.agentType || "DEFAULT",
  });

  const response = await fetch(`${API_BASE_URL}/chat/stream`, {
    method: "POST",
    headers: headers,
    body: JSON.stringify({
      message,
      conversation_history: [], // Backend loads history from DB now
      agentId: options.agentId || null,
      agentType: options.agentType || "DEFAULT",
      sessionId: options.sessionId || null,
    }),
  });

  if (!response.ok) {
    throw new Error(`Backend error: ${response.status} ${response.statusText}`);
  }

  // Parse SSE stream
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // SSE events are separated by double newlines
    const parts = buffer.split("\n\n");
    // Keep the last incomplete part in the buffer
    buffer = parts.pop() || "";

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      // SSE lines start with "data: "
      for (const line of trimmed.split("\n")) {
        if (line.startsWith("data: ")) {
          try {
            const event = JSON.parse(line.slice(6));

            if (event.step === "done") {
              finalResult = event;
            }

            // Fire step callback
            if (onStep) {
              onStep(event);
            }
          } catch (e) {
            console.warn("Failed to parse SSE event:", line, e);
          }
        }
      }
    }
  }

  // If no final "done" event was received, throw
  if (!finalResult) {
    throw new Error("Stream ended without a final response.");
  }

  return finalResult;
}

/**
 * Fetch chat history for a specific agent from the database.
 *
 * @param {number} agentId    - The agent ID
 * @param {string} agentType  - "DEFAULT" or "CUSTOM"
 * @param {Object} options    - Optional overrides
 * @returns {Promise<Object>} - { session, messages }
 */
export async function fetchChatHistory(
  agentId,
  agentType = "DEFAULT",
  options = {},
) {
  const headers = buildHeaders(options);

  try {
    const response = await fetch(
      `${API_BASE_URL}/chat/sessions?agentId=${agentId}&agentType=${agentType}`,
      { headers },
    );

    if (!response.ok) {
      console.warn(`[ChatAPI] fetchChatHistory failed: ${response.status}`);
      return { session: null, messages: [] };
    }

    return await response.json();
  } catch (err) {
    console.warn("[ChatAPI] fetchChatHistory error:", err);
    return { session: null, messages: [] };
  }
}

/**
 * Delete a chat session (clear conversation history).
 *
 * @param {number} sessionId - The session ID to delete
 * @param {Object} options   - Optional overrides
 * @returns {Promise<Object>} - { status, message }
 */
export async function clearChatHistory(sessionId, options = {}) {
  const headers = buildHeaders(options);

  try {
    const response = await fetch(`${API_BASE_URL}/chat/sessions/${sessionId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || `Backend error: ${response.status}`);
    }

    return await response.json();
  } catch (err) {
    console.warn("[ChatAPI] clearChatHistory error:", err);
    throw err;
  }
}

/**
 * Send a chat message (traditional JSON response — fallback).
 *
 * @param {string} message - The user's message
 * @param {Array}  conversationHistory - Previous messages [{role, content}]
 * @param {Object} options - Optional overrides: { userId, companyId, agentId, agentType }
 * @returns {Promise<Object>} - { reply, action, memory, data }
 */
export async function sendChatMessage(
  message,
  conversationHistory = [],
  options = {},
) {
  const response = await fetch(`${API_BASE_URL}/chat`, {
    method: "POST",
    headers: buildHeaders(options),
    body: JSON.stringify({
      message,
      conversation_history: conversationHistory,
      agentId: options.agentId || null,
      agentType: options.agentType || "DEFAULT",
    }),
  });

  if (!response.ok) {
    throw new Error(`Backend error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch all available tools from the backend.
 *
 * @returns {Promise<Array>} - List of tools
 */
export async function fetchTools(options = {}) {
  const headers = buildHeaders(options);

  try {
    const response = await fetch(`${API_BASE_URL}/tools`, {
      headers,
    });

    if (!response.ok) {
      console.warn(`[ChatAPI] fetchTools failed: ${response.status}`);
      return [];
    }

    return await response.json();
  } catch (err) {
    console.warn("[ChatAPI] fetchTools error:", err);
    return [];
  }
}

/**
 * Create a new custom agent.
 *
 * @param {Object} payload - { agentName, description, workflowPrompt, starterQuestions, allowedToolIds }
 * @param {Object} options - Optional overrides: { companyId }
 * @returns {Promise<Object>} - The created agent details
 */
export async function createCustomAgent(payload, options = {}) {
  const headers = buildHeaders(options);

  // Inject companyId from the securely generated headers
  const companyId = parseInt(headers["x-company-id"]) || 1;
  const fullPayload = { ...payload, companyId };

  const response = await fetch(`${API_BASE_URL}/agents/custom`, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(fullPayload),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.detail || `Backend error: ${response.status}`);
  }

  return response.json();
}
